import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// ~/avaai_dashboard/frontend/src/components/Checkers3D.tsx
import { useEffect, useMemo, useState, useRef } from "react";
import "./Checkers3D.css";
const SIZE = 8;
const inBounds = (r, c) => r >= 0 && r < SIZE && c >= 0 && c < SIZE;
const isKing = (p) => p.endsWith("king");
const isColor = (p, color) => (color === "red" ? p.startsWith("red") : p.startsWith("black"));
const opp = (c) => (c === "red" ? "black" : "red");
const cloneBoard = (b) => b.map(row => [...row]);
function initialBoard() {
    const b = Array.from({ length: 8 }, () => Array(8).fill(""));
    for (let r = 0; r < 3; r++)
        for (let c = 0; c < 8; c++)
            if ((r + c) % 2)
                b[r][c] = "black";
    for (let r = 5; r < 8; r++)
        for (let c = 0; c < 8; c++)
            if ((r + c) % 2)
                b[r][c] = "red";
    return b;
}
function maybePromote(piece, toR) {
    if (piece === "red" && toR === 0)
        return { piece: "red-king", promotes: true };
    if (piece === "black" && toR === 7)
        return { piece: "black-king", promotes: true };
    return { piece, promotes: false };
}
/* simple steps (non-captures) */
function genSimpleSteps(b, r, c) {
    const p = b[r][c];
    if (!p)
        return [];
    const out = [];
    if (isKing(p)) {
        for (const [dr, dc] of [[-1, -1], [-1, 1], [1, -1], [1, 1]]) {
            let nr = r + dr, nc = c + dc;
            while (inBounds(nr, nc) && b[nr][nc] === "") {
                out.push({ r: nr, c: nc });
                nr += dr;
                nc += dc;
            }
        }
    }
    else {
        const color = p.startsWith("red") ? "red" : "black";
        const dirs = color === "red" ? [-1] : [1];
        for (const dr of dirs)
            for (const dc of [-1, 1]) {
                const nr = r + dr, nc = c + dc;
                if (inBounds(nr, nc) && b[nr][nc] === "")
                    out.push({ r: nr, c: nc });
            }
    }
    return out;
}
/* captures with multi-jump chaining */
function genCapturesFrom(b, r, c) {
    const p = b[r][c];
    if (!p)
        return [];
    const color = p.startsWith("red") ? "red" : "black";
    const moves = [];
    const stepDirs = isKing(p) ? [-1, 1] : (color === "red" ? [-1] : [1]);
    for (const dr of stepDirs) {
        for (const dc of [-1, 1]) {
            const ar = r + dr, ac = c + dc;
            const lr = r + 2 * dr, lc = c + 2 * dc;
            if (!inBounds(ar, ac) || !inBounds(lr, lc))
                continue;
            const mid = b[ar][ac];
            if (mid && !isColor(mid, color) && b[lr][lc] === "") {
                const nb = cloneBoard(b);
                const piece = nb[r][c];
                nb[r][c] = "";
                nb[ar][ac] = "";
                const res = maybePromote(piece, lr);
                nb[lr][lc] = res.piece;
                if (res.promotes) {
                    moves.push({ color, path: [{ r, c }, { r: lr, c: lc }], captures: [{ r: ar, c: ac }], promotes: true });
                }
                else {
                    const cont = genCapturesFrom(nb, lr, lc);
                    if (!cont.length) {
                        moves.push({ color, path: [{ r, c }, { r: lr, c: lc }], captures: [{ r: ar, c: ac }], promotes: false });
                    }
                    else {
                        for (const m of cont) {
                            moves.push({
                                color,
                                path: [{ r, c }, ...m.path.slice(1)],
                                captures: [{ r: ar, c: ac }, ...m.captures],
                                promotes: m.promotes
                            });
                        }
                    }
                }
            }
        }
    }
    return moves;
}
/* all legal moves with force-jump rule */
function genAllMoves(b, color) {
    const jumps = [], steps = [];
    for (let r = 0; r < SIZE; r++) {
        for (let c = 0; c < SIZE; c++) {
            const p = b[r][c];
            if (!p || !isColor(p, color))
                continue;
            const caps = genCapturesFrom(b, r, c);
            if (caps.length)
                jumps.push(...caps);
            else
                for (const to of genSimpleSteps(b, r, c)) {
                    const res = maybePromote(p, to.r);
                    steps.push({ color, path: [{ r, c }, to], captures: [], promotes: res.piece !== p });
                }
        }
    }
    return jumps.length ? jumps : steps;
}
function applyMove(b, m) {
    const nb = cloneBoard(b);
    const from = m.path[0];
    let piece = nb[from.r][from.c];
    nb[from.r][from.c] = "";
    for (let i = 1; i < m.path.length; i++) {
        const sq = m.path[i];
        if (i === m.path.length - 1) {
            const res = maybePromote(piece, sq.r);
            piece = res.piece;
        }
        nb[sq.r][sq.c] = piece;
        const prev = m.path[i - 1];
        if (!(prev.r === from.r && prev.c === from.c))
            nb[prev.r][prev.c] = "";
    }
    for (const cap of m.captures)
        nb[cap.r][cap.c] = "";
    return nb;
}
function evaluate(b) {
    let score = 0, redCount = 0, blackCount = 0;
    for (let r = 0; r < SIZE; r++)
        for (let c = 0; c < SIZE; c++) {
            const p = b[r][c];
            if (!p)
                continue;
            const v = (isKing(p) ? 175 : 100) + (r >= 2 && r <= 5 && c >= 2 && c <= 5 ? 6 : 0);
            if (p.startsWith("red")) {
                score += v;
                redCount++;
            }
            else {
                score -= v;
                blackCount++;
            }
        }
    score += (genAllMoves(b, "red").length - genAllMoves(b, "black").length) * 5;
    if (redCount === 0)
        score -= 10000;
    if (blackCount === 0)
        score += 10000;
    return score;
}
function minimax(b, depth, toMove, alpha, beta) {
    if (depth === 0)
        return { score: evaluate(b) };
    const moves = genAllMoves(b, toMove);
    if (!moves.length)
        return { score: toMove === "red" ? -10000 : 10000 };
    if (toMove === "red") {
        let best = -Infinity, bestMove;
        for (const m of moves) {
            const { score } = minimax(applyMove(b, m), depth - 1, "black", alpha, beta);
            if (score > best) {
                best = score;
                bestMove = m;
            }
            alpha = Math.max(alpha, best);
            if (beta <= alpha)
                break;
        }
        return { score: best, move: bestMove };
    }
    else {
        let best = Infinity, bestMove;
        for (const m of moves) {
            const { score } = minimax(applyMove(b, m), depth - 1, "red", alpha, beta);
            if (score < best) {
                best = score;
                bestMove = m;
            }
            beta = Math.min(beta, best);
            if (beta <= alpha)
                break;
        }
        return { score: best, move: bestMove };
    }
}
function chooseAIMove(b, depth, aiColor) {
    const moves = genAllMoves(b, aiColor);
    if (!moves.length)
        return null;
    const ordered = [...moves].sort((a, b) => b.captures.length - a.captures.length);
    let bestScore = aiColor === "red" ? -Infinity : Infinity;
    let best = null;
    for (const m of ordered) {
        const { score } = minimax(applyMove(b, m), depth - 1, opp(aiColor), -Infinity, Infinity);
        if (aiColor === "red" ? score > bestScore : score < bestScore) {
            bestScore = score;
            best = m;
        }
    }
    return best;
}
export default function Checkers3D({ onExit }) {
    const [board, setBoard] = useState(initialBoard());
    const [turn, setTurn] = useState("red");
    const [winner, setWinner] = useState(null);
    const [playerColor, setPlayerColor] = useState(null);
    const [aiColor, setAiColor] = useState(null);
    const [aiDepth, setAiDepth] = useState(5);
    const [selected, setSelected] = useState(null);
    const [forcedChainFrom, setForcedChainFrom] = useState(null);
    // ---- responsive board sizing (critical) ----
    const [boardPx, setBoardPx] = useState(420);
    const [topPad, setTopPad] = useState(8);
    const [bottomPad, setBottomPad] = useState(100);
    const resizeObs = useRef(null);
    const computeBoardSize = () => {
        const taskbar = document.querySelector(".taskbar");
        const chatbox = document.querySelector(".chatbox-inner");
        const top = (taskbar?.offsetHeight ?? 0);
        const bottom = (chatbox?.offsetHeight ?? 0);
        setTopPad(top > 0 ? 8 : 0);
        setBottomPad(bottom + 16);
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const availW = vw - 32; // small side gutters
        const availH = vh - top - bottom - 32; // keep above/below bars
        const size = Math.max(280, Math.min(availW, availH)); // clamp min
        setBoardPx(size);
    };
    useEffect(() => {
        computeBoardSize();
        const onResize = () => computeBoardSize();
        window.addEventListener("resize", onResize);
        window.addEventListener("orientationchange", onResize);
        // Watch the bars if their height changes
        resizeObs.current = new ResizeObserver(() => computeBoardSize());
        const t = document.querySelector(".taskbar");
        const c = document.querySelector(".chatbox-inner");
        if (t)
            resizeObs.current.observe(t);
        if (c)
            resizeObs.current.observe(c);
        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("orientationchange", onResize);
            resizeObs.current?.disconnect();
        };
    }, []);
    const checkWinner = (b) => {
        const reds = b.flat().filter(x => x && x.startsWith("red")).length;
        const blacks = b.flat().filter(x => x && x.startsWith("black")).length;
        if (reds === 0)
            setWinner("Black");
        else if (blacks === 0)
            setWinner("Red");
        else if (!genAllMoves(b, "red").length)
            setWinner("Black");
        else if (!genAllMoves(b, "black").length)
            setWinner("Red");
    };
    // click handler (force jump + chains)
    const handleCellClick = (r, c) => {
        if (winner || !playerColor || turn !== playerColor)
            return;
        const cell = board[r][c];
        const movesForColor = genAllMoves(board, playerColor);
        const hasJump = movesForColor.some(m => m.captures.length);
        if (forcedChainFrom) {
            if (!selected) {
                if (r === forcedChainFrom.r && c === forcedChainFrom.c)
                    setSelected({ r, c });
                return;
            }
            else {
                const contMoves = genCapturesFrom(board, forcedChainFrom.r, forcedChainFrom.c);
                const match = contMoves.find(m => m.path.length >= 2 && m.path[1].r === r && m.path[1].c === c);
                if (match) {
                    const step = {
                        color: playerColor,
                        path: [forcedChainFrom, { r, c }],
                        captures: [match.captures[0]],
                        promotes: match.promotes && match.path[match.path.length - 1].r === r && match.path[match.path.length - 1].c === c
                    };
                    const nb = applyMove(board, step);
                    setBoard(nb);
                    if (step.promotes) {
                        setForcedChainFrom(null);
                        setSelected(null);
                        setTurn(opp(playerColor));
                        checkWinner(nb);
                        return;
                    }
                    const next = genCapturesFrom(nb, r, c);
                    if (next.length) {
                        setForcedChainFrom({ r, c });
                        setSelected({ r, c });
                    }
                    else {
                        setForcedChainFrom(null);
                        setSelected(null);
                        setTurn(opp(playerColor));
                    }
                    checkWinner(nb);
                }
                return;
            }
        }
        if (!selected) {
            if (cell && isColor(cell, playerColor)) {
                if (hasJump) {
                    if (genCapturesFrom(board, r, c).length)
                        setSelected({ r, c });
                }
                else
                    setSelected({ r, c });
            }
            return;
        }
        const from = selected;
        const legalFrom = movesForColor.filter(m => m.path[0].r === from.r && m.path[0].c === from.c);
        const filtered = hasJump ? legalFrom.filter(m => m.captures.length) : legalFrom;
        let chosen = null;
        if (!hasJump) {
            chosen = filtered.find(m => m.path.length === 2 && m.path[1].r === r && m.path[1].c === c) || null;
        }
        else {
            chosen = filtered.find(m => m.path.length >= 2 && m.path[1].r === r && m.path[1].c === c) || null;
        }
        if (!chosen) {
            setSelected(null);
            return;
        }
        const firstStep = {
            color: playerColor,
            path: chosen.path.slice(0, 2),
            captures: chosen.captures.slice(0, 1),
            promotes: chosen.promotes && chosen.path.length === 2
        };
        const nb = applyMove(board, firstStep);
        setBoard(nb);
        if (firstStep.captures.length) {
            if (firstStep.promotes) {
                setForcedChainFrom(null);
                setSelected(null);
                setTurn(opp(playerColor));
            }
            else {
                const cont = genCapturesFrom(nb, r, c);
                if (cont.length) {
                    setForcedChainFrom({ r, c });
                    setSelected({ r, c });
                }
                else {
                    setForcedChainFrom(null);
                    setSelected(null);
                    setTurn(opp(playerColor));
                }
            }
        }
        else {
            setForcedChainFrom(null);
            setSelected(null);
            setTurn(opp(playerColor));
        }
        checkWinner(nb);
    };
    // Ava turn
    useEffect(() => {
        if (winner || !playerColor || !aiColor)
            return;
        if (turn !== aiColor)
            return;
        const id = setTimeout(() => {
            const move = chooseAIMove(board, aiDepth, aiColor);
            if (!move) {
                setWinner(playerColor === "red" ? "Red" : "Black");
                return;
            }
            const nb = applyMove(board, move);
            setBoard(nb);
            setTurn(playerColor);
            checkWinner(nb);
        }, 400);
        return () => clearTimeout(id);
    }, [turn, board, winner, playerColor, aiColor, aiDepth]);
    const selectableSet = useMemo(() => {
        if (!playerColor || turn !== playerColor || winner)
            return new Set();
        const moves = genAllMoves(board, playerColor);
        const hasJump = moves.some(m => m.captures.length);
        const sel = new Set();
        for (const m of moves) {
            if (hasJump && !m.captures.length)
                continue;
            sel.add(`${m.path[0].r},${m.path[0].c}`);
        }
        return sel;
    }, [board, playerColor, turn, winner]);
    const isSelectedCell = (r, c) => selected && selected.r === r && selected.c === c;
    const resetGame = () => {
        setBoard(initialBoard());
        setTurn("red");
        setWinner(null);
        setPlayerColor(null);
        setAiColor(null);
        setSelected(null);
        setForcedChainFrom(null);
        // recompute size again after reset
        setTimeout(computeBoardSize, 0);
    };
    return (_jsx("div", { className: "checkers-game", style: { paddingTop: topPad, marginBottom: bottomPad }, children: !playerColor ? (_jsxs("div", { className: "checkers-setup", children: [_jsx("h2", { className: "neon-text", children: "Choose your color" }), _jsxs("div", { className: "checkers-buttons", children: [_jsx("button", { onClick: () => { setPlayerColor("red"); setAiColor("black"); }, children: "Play as Red" }), _jsx("button", { onClick: () => { setPlayerColor("black"); setAiColor("red"); setTurn("red"); }, children: "Play as Black" })] }), _jsxs("div", { className: "checkers-controls", style: { marginTop: "0.6rem" }, children: [_jsxs("label", { className: "neon-text", style: { display: "block", marginBottom: 6 }, children: ["Ava Difficulty (depth): ", aiDepth] }), _jsx("input", { type: "range", min: 2, max: 8, step: 1, value: aiDepth, onChange: (e) => setAiDepth(parseInt(e.target.value, 10)) })] }), _jsx("div", { className: "checkers-buttons", style: { marginTop: "0.8rem" }, children: _jsx("button", { onClick: onExit, children: "Exit" }) })] })) : winner ? (_jsxs("div", { className: "checkers-finish", children: [_jsxs("h2", { className: "neon-text", children: [winner, " wins!"] }), _jsxs("div", { className: "checkers-buttons", children: [_jsx("button", { onClick: resetGame, children: "Play Again" }), _jsx("button", { onClick: onExit, children: "Exit" })] })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "checkers-setup neon-text", children: ["Turn: ", turn, forcedChainFrom && _jsx("span", { style: { marginLeft: 10 }, children: "(continue jumps!)" }), _jsx("button", { onClick: onExit, style: { marginLeft: 12 }, children: "Exit" })] }), _jsx("div", { className: "checkers-board", style: { width: boardPx, height: boardPx }, children: board.map((row, r) => row.map((cell, c) => {
                        const isDark = (r + c) % 2 === 1;
                        const glow = isSelectedCell(r, c) || (selectableSet.has(`${r},${c}`) && turn === playerColor);
                        return (_jsx("div", { className: `checkers-cell ${isDark ? "dark" : "light"} ${glow ? "selected" : ""}`, onClick: () => handleCellClick(r, c), children: cell && (_jsx("div", { className: `piece ${cell.startsWith("red") ? "red" : "black"}`, children: isKing(cell) && _jsx("span", { className: "crown", children: "\u2655" }) })) }, `${r}-${c}`));
                    })) })] })) }));
}
